import { useState, useRef } from "react";
import { message } from "antd";
import { v4 as uuidv4 } from "uuid";
import db from "../../../../database";
import { SyncData } from "../Tables-Management/SyncData";
import { getWorkflowData } from "../../../../utils/getWorkflowData";
import { useEventBus } from "../../../../lib/components/EventBusProvider";
import usePOSConfigStore from "../../../../stores/usePOSConfigStore";
import useRestaurantStore from "../../../../stores/useRestaurantStore";
import { roundTo } from "../../../../utils/tillHelpers";
import { getIsInterState } from "../../../../utils/taxUtils";
import { groupItemsByTaxRate, splitLineByQuantity } from "../../Core/utils/taxCalculations";
import { hasActiveOrdersForTable } from "../../../../utils/restaurantOrderCleanup";
import { trace } from "../../../../utils/debugTrace";
import useCartStore from "../../../../stores/useCartStore";
import {
  getOpenPreparedBillsForSession,
  cancelSupersededPreparedBills,
  isTableBillRequested,
  PREPARED_BILL_LOCKED_MESSAGE,
  BILL_REQUESTED_LOCKED_MESSAGE,
} from "../../../../utils/preparedBillTableLock";
import { tracePreparedBill } from "../../../../utils/preparedBillRecovery";

/**
 * Split a fired order-line record (SyncData/upsertFbOrder schema — qty/lineNet/lineGross/
 * lineStd/netStd/discount, distinct from the cart-item weight/nettotal/taxAmount schema) into
 * a remainder and a cancelled fragment, by quantity ratio. Per-unit fields (unitPrice, listPrice,
 * grossStd, grossUnit, netList, netUnit, unitTax, lineTax=taxRate) are identity/unit values and
 * carry over unchanged; per-line totals are ratio-split, remainder computed as original-minus-
 * cancelled to avoid rounding drift. Also patches the embedded "orderLine" meta JSON (consumed by
 * useOrderHandlers.js's [BUG-1504] reconciliation fallback when injecting a line missing from the
 * cached cart snapshot) so a stale-snapshot reload reconstructs the fragment correctly.
 */
const splitOrderLineByQuantity = (line, cancelQty, newFbOrderLineId, newLineId, precision) => {
  const totalQty = parseFloat(line.qty) || 0;
  const keepQty = totalQty - cancelQty;
  const ratio = totalQty > 0 ? cancelQty / totalQty : 0;

  const cancelledOrderLine = { ...line };
  const remainderOrderLine = { ...line };

  ["discount", "lineNet", "lineGross", "lineStd", "netStd"].forEach((field) => {
    const total = parseFloat(line[field] || 0);
    const cancelledPart = roundTo(total * ratio, precision);
    cancelledOrderLine[field] = cancelledPart;
    remainderOrderLine[field] = roundTo(total - cancelledPart, precision);
  });

  cancelledOrderLine.qty = cancelQty;
  cancelledOrderLine.fbOrderLineId = newFbOrderLineId;
  cancelledOrderLine.lineId = newLineId;
  cancelledOrderLine.isCanceled = "Y";

  remainderOrderLine.qty = keepQty;

  const patchMeta = (orderLine, patch) => {
    const metaEntry = orderLine.meta?.find((m) => m.key === "orderLine");
    if (!metaEntry) return orderLine;
    let parsed = {};
    try {
      parsed = metaEntry.value ? JSON.parse(metaEntry.value) : {};
    } catch (e) {
      parsed = {};
    }
    return {
      ...orderLine,
      meta: [{ ...metaEntry, value: JSON.stringify({ ...parsed, ...patch }), fbOrderLineMetaId: uuidv4().replace(/-/g, "").toUpperCase() }],
    };
  };

  return {
    remainderOrderLine: patchMeta(remainderOrderLine, { weight: keepQty, firedQty: keepQty, sendQty: 0 }),
    cancelledOrderLine: patchMeta(cancelledOrderLine, {
      lineId: newLineId,
      fbOrderLineId: newFbOrderLineId,
      weight: cancelQty,
      firedQty: cancelQty,
      sendQty: 0,
      isCanceled: "Y",
      orderStatus: "CL",
    }),
  };
};

/**
 * Provides handleVoid — the KOT cancellation handler with workflow approval,
 * cart recalculation, table/split sync, and DB persistence.
 */
const useVoidHandler = ({
  cart,
  setCart,
  history,
  setKeyValue,
  setShowProducts,
  tillData,
  precision,
  setCategorieFlag,
  clearCart1,
  setTableCards,
  setOcupiedTablesData,
  setReservedTablesData,
  setTableList,
  setLoading,
}) => {
  const { dispatchEvent } = useEventBus();
  // `cancelling` state drives the UI (disables the table grid / cancel button while
  // a void is in flight — see TableLayout.js and Restaurant/index.js). It must NOT
  // be the in-flight guard itself: setCancelling(true) does not update the captured
  // `cancelling` closure synchronously, so two rapid clicks both read false and both
  // proceed, double-applying the cancellation. The ref is the actual guard (same
  // pattern as the parkedBillInFlightRef / paymentInProgressRef double-submit guards
  // elsewhere in this codebase); the state exists purely for rendering.
  const [cancelling, setCancelling] = useState(false);
  const cancellingRef = useRef(false);

  // TSK-2932: approval is mandatory only for a FULL order cancellation (Cancel All,
  // or Select All + Cancel Selected) — a partial item-level cancel just needs its
  // mandatory remark (enforced in CancelOrderItemsModal), not manager approval.
  // "Full" is judged against what's actually left cancellable on the persisted
  // order, not against the caller's own idea of "all" — so it stays correct even
  // if the UI's cart snapshot is stale relative to db.fbOrderData.
  const isFullOrderCancellation = async (record, cancelItemIds) => {
    const dineInKey = useRestaurantStore.getState().dineIn;
    if (dineInKey !== "Y" || !Array.isArray(cancelItemIds) || cancelItemIds.length === 0) return true;

    const orders = await db.fbOrderData.where("cwrFbTableId").equals(record?.cwrFbTableId).toArray();
    const ordersData = orders.find((order) => order.fbOrderStatus === "IP");
    if (!ordersData) return true;

    const sourceItems = ordersData.cart?.items?.length ? ordersData.cart.items : ordersData.wsData?.items || [];
    const cancellableIds = sourceItems
      .filter((item) => item.isCanceled !== "Y" && !["RC", "CO", "CL"].includes(item.orderStatus))
      .map((item) => item.fbOrderLineId);

    if (cancellableIds.length === 0) return true;
    return cancellableIds.every((id) => cancelItemIds.includes(id));
  };

  // TIC-11390: the table's open Prepare Bill, read from db.orders. The Cancel button's own
  // guard only sees the on-screen cart's preparedBillOrigin, which a reopened cart can lack
  // (HMRS/10821 cancelled this way; HMRS/10843 had a line cancelled and was re-billed).
  const getTablePreparedBills = async (record) => {
    const tableId = record?.cwrFbTableId;
    if (!tableId || useRestaurantStore.getState().dineIn !== "Y") return [];
    const tableOrders = await db.fbOrderData.where("cwrFbTableId").equals(tableId).toArray();
    const ip = tableOrders.find((order) => order.fbOrderStatus === "IP");
    const liveCart = useCartStore.getState().cart || {};
    return getOpenPreparedBillsForSession({
      tableId,
      items: [...(ip?.lines || []), ...(ip?.cart?.items || []), ...(ip?.wsData?.items || []), ...(liveCart.tableId === tableId ? liveCart.items || [] : [])],
    });
  };

  // Returns true only once the cancellation has actually been written (approval
  // granted where required) — callers must not act on cancellation side effects
  // (e.g. printing a "cancelled" KOT slip) until this resolves true.
  const handleVoid = async (record, cancelItemIds, lineRemarks = {}, cancelQtyMap = {}) => {
    if (cancellingRef.current) return false;
    cancellingRef.current = true;
    setCancelling(true);
    let cancellationApplied = false;
    let cartBillSOrderID;
    try {
      const preparedBills = await getTablePreparedBills(record);
      if (preparedBills.length > 0) {
        const posConfigNow = usePOSConfigStore.getState().posConfig;
        if (posConfigNow?.allowModificationAfterPrepareBill !== "Y") {
          message.error(PREPARED_BILL_LOCKED_MESSAGE(preparedBills[0].documentno));
          tracePreparedBill("cancel-kot-blocked-prepared-bill", {
            tableId: record?.cwrFbTableId,
            bills: preparedBills.map((b) => ({ sOrderID: b.sOrderID, documentno: b.documentno })),
            cartSOrderID: useCartStore.getState().cart?.sOrderID,
            cartHasFlag: useCartStore.getState().cart?.preparedBillOrigin === "Y",
          });
          return false;
        }
        // Modification allowed on this till: a full cancel must cancel the prepared bill under
        // its own number. clearCart1 does that only for a cart flagged preparedBillOrigin, so
        // restore the flag when the cart is that bill (HMRS/10821 took a fresh HMRS/10934).
        const liveCart = useCartStore.getState().cart || {};
        const sameBill = preparedBills.find((b) => b.sOrderID === liveCart.sOrderID);
        cartBillSOrderID = sameBill?.sOrderID;
        if (sameBill && liveCart.preparedBillOrigin !== "Y") {
          useCartStore.getState().setCart((prev) => (prev.sOrderID === sameBill.sOrderID ? { ...prev, preparedBillOrigin: "Y", documentno: sameBill.documentno } : prev));
        }
      }
      // BUG-2575: no bill row on this device (prepared on another one, or moved here by Transfer
      // Bill), but the table itself says a bill was prepared.
      if (
        preparedBills.length === 0 &&
        useRestaurantStore.getState().dineIn === "Y" &&
        usePOSConfigStore.getState().posConfig?.allowModificationAfterPrepareBill !== "Y" &&
        (await isTableBillRequested(record?.cwrFbTableId))
      ) {
        message.error(BILL_REQUESTED_LOCKED_MESSAGE);
        tracePreparedBill("cancel-kot-blocked-bill-requested", {
          tableId: record?.cwrFbTableId,
          cartSOrderID: useCartStore.getState().cart?.sOrderID,
        });
        return false;
      }
      const requiresApproval = await isFullOrderCancellation(record, cancelItemIds);
      // TIC-11390 (modification allowed): a full cancel ends the table session — no open prepared
      // bill of it may stay completed in CW. The cart's own bill is cancelled by clearCart1; every
      // other one is cancelled here, BEFORE clearCart1, whose immediate sync then sends them too.
      const proceed = async () => {
        if (requiresApproval && preparedBills.length > 0) {
          await cancelSupersededPreparedBills({
            tableId: record?.cwrFbTableId,
            items: preparedBills.flatMap((b) => b.items || []),
            keepSOrderID: cartBillSOrderID,
            flow: "full-cancel",
          });
        }
        await proceedWithCancellation(record, lineRemarks, cancelQtyMap);
      };

      if (requiresApproval) {
        const { workflowRules, approvers } = await getWorkflowData();
        const cancelKotRule = workflowRules.find((res) => res.eventName === "Cancel KOT");

        if (cancelKotRule) {
          await new Promise((resolve) => {
            dispatchEvent(cancelKotRule.eventName, cancelKotRule.ruleName, cancelKotRule.ruleCondition, approvers, null, async (eventData) => {
              try {
                if (eventData?.approvalGranted) {
                  await proceed();
                  cancellationApplied = true;
                }
                // BUG-1861: no explicit "Reject" action exists in the approval drawer —
                // closing it (X icon) is the only way out when not approving, so treat
                // that as a silent dismissal rather than surfacing an error alert.
              } finally {
                resolve();
              }
            });
          });
        } else {
          await proceed();
          cancellationApplied = true;
        }
      } else {
        await proceed();
        cancellationApplied = true;
      }
    } finally {
      cancellingRef.current = false;
      setCancelling(false);
    }

    return cancellationApplied;

    async function proceedWithCancellation(record, lineRemarks = {}, cancelQtyMap = {}) {
      let shouldClearCart = false;
      const dineInKey = useRestaurantStore.getState().dineIn;

      if (dineInKey === "Y") {
        const orders = await db.fbOrderData.where("cwrFbTableId").equals(record.cwrFbTableId).toArray();
        const ordersData = orders.find((order) => order.fbOrderStatus === "IP");
        const ordersFetched = ordersData ? [ordersData] : [];

        for (const fbOrder of ordersFetched) {
          if (fbOrder.fbOrderStatus !== "IP") continue;

          // [BUG-1921] fbOrder.cart (db.fbOrderData.cart) is only ever written when THIS
          // terminal itself fires an item (useOrderHandlers.js/useCopyKOT.js) — every
          // ORDER_UPSERT write only touches .lines/.wsData, never .cart (see
          // useWebSocketHandler.js). A table whose items were all added/fired from
          // elsewhere (Mobile App, another terminal) has NO fbOrder.cart on this terminal
          // at all. The old fallback to fbOrder.wsData is itself unreliable: wsData is
          // parsed from the last "cartDetails" meta snapshot, which can lag badly behind
          // reality (confirmed via trace: a 4-item live order had a wsData snapshot still
          // showing only 2 items, from before the other 2 were even added) — falling back
          // to it silently dropped the missing items from the cancel-recomputed cart, and
          // from the bill. The live `cart` prop is what the cashier is actually looking at
          // and cancelling items FROM right now, so prefer it whenever it's at least as
          // complete as fbOrder.cart, before ever falling back to the stale wsData snapshot.
          const dbCartItemCount = fbOrder.cart?.items?.length || 0;
          const liveCartItemCount = cart?.items?.length || 0;
          if ((!fbOrder.cart || liveCartItemCount > dbCartItemCount) && liveCartItemCount > 0) {
            trace("BUG-1921", "handleVoid: using live cart as fbOrder.cart base", { fbOrderId: fbOrder.fbOrderId, dbCartItemCount, liveCartItemCount });
            // [BUG-1921 / F-38] Deep-clone — a shallow `{ ...cart }` still shares `items`
            // (and each item object) with the live Zustand cart. The recompute loop below
            // mutates items in place (item.discount = ..., Object.assign(item, {...})),
            // which would otherwise corrupt the live, still-editable cart before setCart()
            // ever runs. See useOrderHandlers.js:290-291 for the same pattern on this cart.
            fbOrder.cart = structuredClone(cart);
          } else if (!fbOrder.cart && fbOrder.wsData && typeof fbOrder.wsData === "object" && Object.keys(fbOrder.wsData).length > 0) {
            trace("BUG-1921", "handleVoid: no fbOrder.cart and no usable live cart — falling back to stale wsData snapshot", {
              fbOrderId: fbOrder.fbOrderId, wsDataItemCount: fbOrder.wsData?.items?.length || 0,
            });
            fbOrder.cart = { ...fbOrder.wsData };
          }

          if (!Array.isArray(fbOrder.lines) || fbOrder.lines.length === 0) {
            console.warn("[handleVoid] fbOrder.lines is empty or missing, skipping:", fbOrder.fbOrderId);
            continue;
          }

          if (!Array.isArray(cancelItemIds) || cancelItemIds.length === 0) {
            console.warn("[handleVoid] cancelItemIds is empty or invalid, aborting cancellation");
            return;
          }

          const cancelRemarksData = useRestaurantStore.getState().cancelKOTRemarks || {};
          const globalRemarks = cancelRemarksData?.remarks || "";

          // Recalculate summaries — taxIncludeFlag is needed up-front now since a partial
          // cancel (TSK-2931) has to compute split money fields before the summary pass below.
          const saleTypeData = usePOSConfigStore.getState().saleTypeData || {};
          const taxIncludeFlag = saleTypeData?.customer?.istaxincluded === "Y" ? "Y" : tillData.tillAccess.csBunit.isTaxIncluded ? tillData.tillAccess.csBunit.isTaxIncluded : "Y";

          // [TSK-2931] Build the cancel plan once, so fbOrder.lines / cart.items / wsData.items
          // all split the same original line into the same remainder + cancelled-fragment
          // identity (shared fbOrderLineId/lineId).
          // [BUG-1802] The plan carries the quantity the cashier actually chose (cancelQtyMap);
          // partial-vs-full is NOT decided here from cart.items[].weight. After a re-fire from
          // the mobile app, ORDER_UPSERT refreshes lines/wsData but never cart, so cart.items
          // can hold a pre-refire quantity (2) while lines/wsData hold the consolidated one (5).
          // Deciding "isPartial" once against that stale total made `cancelQty < totalQty` false
          // and silently escalated a partial cancel into a whole-line cancel of the full
          // quantity. Each source now resolves the plan against its OWN quantity below.
          // An id absent from cancelQtyMap (e.g. "Cancel All") still means "cancel it all".
          const splitPlan = new Map();
          for (const fbOrderLineId of cancelItemIds) {
            const requestedQty = parseFloat(cancelQtyMap?.[fbOrderLineId]);
            if (!isFinite(requestedQty) || requestedQty <= 0) continue;
            splitPlan.set(fbOrderLineId, {
              cancelQty: requestedQty,
              newFbOrderLineId: uuidv4().replace(/-/g, "").toUpperCase(),
              newLineId: uuidv4().replace(/-/g, "").toUpperCase(),
            });
          }

          // Resolve the plan against one record's own quantity. Returns null when the whole
          // record must be cancelled — no qty chosen, an unreadable qty, or a requested qty
          // that already covers everything this record holds (the pre-BUG-1802 behavior).
          const resolvePlan = (fbOrderLineId, ownQty) => {
            const plan = splitPlan.get(fbOrderLineId);
            const totalQty = parseFloat(ownQty);
            if (!plan || !isFinite(totalQty) || totalQty <= 0) return null;
            return plan.cancelQty < totalQty ? plan : null;
          };

          // [BUG-1802] Capture the quantity each source holds for the lines being cancelled.
          // A mismatch between cartWeight / wsWeight / lineQty is what turned a partial cancel
          // into a full one; logging it here makes any remaining divergence self-evident.
          if (splitPlan.size > 0) {
            trace("BUG-1802", "partial cancel plan", {
              lines: [...splitPlan.keys()].map((id) => ({
                id,
                cancelQty: splitPlan.get(id).cancelQty,
                lineQty: fbOrder.lines?.find((l) => l.fbOrderLineId === id)?.qty,
                cartWeight: fbOrder.cart?.items?.find((i) => i.fbOrderLineId === id)?.weight,
                wsWeight: fbOrder.wsData?.items?.find((i) => i.fbOrderLineId === id)?.weight,
              })),
            });
          }

          fbOrder.lines = fbOrder.lines.flatMap((line) => {
            const isAlreadyFinal = line.isCanceled === "Y";
            if (isAlreadyFinal || !cancelItemIds.includes(line.fbOrderLineId)) return [line];
            const remarks = lineRemarks?.[line.fbOrderLineId] || globalRemarks;
            // [BUG-1802] resolve against this line's own qty — the fired quantity the server
            // holds, which a mobile re-fire can have raised above the cached cart snapshot.
            const plan = resolvePlan(line.fbOrderLineId, line.qty);
            if (!plan) {
              return [{ ...line, isCanceled: "Y", remarks }];
            }
            const { remainderOrderLine, cancelledOrderLine } = splitOrderLineByQuantity(line, plan.cancelQty, plan.newFbOrderLineId, plan.newLineId, precision);
            return [remainderOrderLine, { ...cancelledOrderLine, remarks }];
          });

          if (fbOrder.cart?.items?.length) {
            fbOrder.cart.items = fbOrder.cart.items.flatMap((item) => {
              const isAlreadyFinal = ["RC", "CO", "CL"].includes(item.orderStatus);
              if (isAlreadyFinal || !cancelItemIds.includes(item.fbOrderLineId)) return [item];
              const plan = resolvePlan(item.fbOrderLineId, item.weight);
              if (!plan) {
                return [{ ...item, isCanceled: "Y", orderStatus: "CL" }];
              }
              const remarks = lineRemarks?.[item.fbOrderLineId] || globalRemarks;
              const { remainderLine, cancelledFragment } = splitLineByQuantity(item, plan.cancelQty, taxIncludeFlag, precision, {
                newLineId: plan.newLineId,
                newFbOrderLineId: plan.newFbOrderLineId,
              });
              return [remainderLine, { ...cancelledFragment, remarks }];
            });
          }

          const itemsSummery = {
            decimalItems: { totalQty: 0, totalAmount: 0 },
            nonDecimalItems: { totalQty: 0, totalAmount: 0 },
            itemsQty: 0,
            items: 0,
          };

          const isInterState = getIsInterState(fbOrder.cart);
          const groupedItemsByTaxRate = groupItemsByTaxRate(fbOrder.cart?.items || [], precision, { isInterState, filterCanceled: true });

          let updatedTotalTax = 0;
          let updatedTotalPrice = 0;
          let updatedTotalItemsQty = 0;
          let updatedTotalDiscounts = 0;
          let updatedTotalStd = 0;

          if (fbOrder.cart?.items?.length) {
            fbOrder.cart.items = fbOrder.cart.items.map((item, i) => {
              if (item.isCanceled === "Y") {
                item.discount = 0;
                item.key = i;
                return item;
              }
              const nettotalFixed = parseFloat(parseFloat(item?.nettotal)?.toFixed(precision));
              const taxAmountFixed = parseFloat(parseFloat(item?.taxAmount)?.toFixed(precision));
              const discountFixed = item.discount ? parseFloat(parseFloat(item?.discount)?.toFixed(precision)) : 0;

              updatedTotalPrice += nettotalFixed;
              updatedTotalItemsQty += item.weight;
              updatedTotalTax += taxAmountFixed;
              updatedTotalDiscounts += discountFixed;

              item.discount = discountFixed;
              item.key = i;

              if (!item.isGiftCard) {
                const unitPrice = isFinite(item.nettotal / item.weight) ? (taxIncludeFlag === "Y" ? item.nettotal / item.weight / (1 + item.taxRate / 100) : item.salePrice) : 0;
                const lineTax = item.listPrice * (1 + (item.taxRate || 0) / 100);
                const stdTax = item.sunitprice * (1 + (item.taxRate || 0) / 100);
                const netList =
                  taxIncludeFlag === "Y" ? item.listPrice - parseFloat(parseFloat(item.listPrice - item.listPrice / (1 + item.taxRate / 100))?.toFixed(precision)) : item.listPrice;
                const gross_list = taxIncludeFlag === "Y" ? item.listPrice : parseFloat(parseFloat(lineTax)?.toFixed(precision));
                const grossStd = taxIncludeFlag === "Y" ? item.sunitprice : parseFloat(parseFloat(stdTax)?.toFixed(precision));

                Object.assign(item, {
                  linetax: nettotalFixed ? taxAmountFixed : 0,
                  linenet: nettotalFixed ? parseFloat(parseFloat(item.nettotal - item.taxAmount)?.toFixed(precision)) : 0,
                  linegross: nettotalFixed || 0,
                  linestd: nettotalFixed ? parseFloat(parseFloat(grossStd * item.weight)?.toFixed(precision)) : 0,
                  netunit: nettotalFixed ? parseFloat(parseFloat(unitPrice)?.toFixed(precision)) : 0,
                  listprice: nettotalFixed ? item.listPrice : 0,
                  grossunit: nettotalFixed ? parseFloat(parseFloat(unitPrice)?.toFixed(precision)) * (item.isReturn ? -1 : 1) : 0,
                  grossstd: nettotalFixed ? grossStd : 0,
                  grosslist: nettotalFixed ? roundTo(gross_list, precision) : 0,
                  netList: nettotalFixed ? netList : 0,
                  unitPrice: nettotalFixed ? parseFloat(parseFloat(unitPrice)?.toFixed(precision)) : 0,
                  taxAmount: nettotalFixed ? taxAmountFixed : 0,
                });

                updatedTotalStd += nettotalFixed ? parseFloat(parseFloat(grossStd * item.weight)?.toFixed(precision)) : 0;
              }

              if (Array.isArray(item.children) && item.children.length > 0) {
                item.children.forEach((child) => {
                  const isDecimal = child.uomData?.[0]?.decimal === "Y" || child.isDecimal;
                  const summaryKey = isDecimal ? "decimalItems" : "nonDecimalItems";
                  itemsSummery.itemsQty += child.weight;
                  itemsSummery.items += 1;
                  itemsSummery[summaryKey].totalQty += child.weight;
                  itemsSummery[summaryKey].totalAmount += parseFloat(parseFloat(child.nettotal)?.toFixed(precision));
                });
              } else {
                const isDecimal = item.uomData?.[0]?.decimal === "Y" || item.isDecimal;
                const summaryKey = isDecimal ? "decimalItems" : "nonDecimalItems";
                itemsSummery.itemsQty += item.weight;
                itemsSummery.items += 1;
                itemsSummery[summaryKey].totalQty += item.weight;
                itemsSummery[summaryKey].totalAmount += parseFloat(parseFloat(item.nettotal)?.toFixed(precision));
              }

              return item;
            });

            fbOrder.cart.total = parseFloat(updatedTotalPrice.toFixed(precision));
            fbOrder.cart.tax = updatedTotalTax;
            fbOrder.cart.discount = updatedTotalDiscounts;
            fbOrder.cart.totalQty = updatedTotalItemsQty;
            fbOrder.cart.subTotal = parseFloat(parseFloat(updatedTotalPrice - updatedTotalTax)?.toFixed(precision));
            fbOrder.cart.totalStd = updatedTotalStd;
            fbOrder.cart.itemsSummery = itemsSummery;
            fbOrder.cart.groupedItemsByTaxRate = groupedItemsByTaxRate;
          }

          if (fbOrder.wsData && typeof fbOrder.wsData === "object" && Object.keys(fbOrder.wsData).length > 0) {
            if (Array.isArray(fbOrder.wsData.items) && fbOrder.wsData.items.length > 0) {
              fbOrder.wsData = {
                ...fbOrder.wsData,
                items: fbOrder.wsData.items.flatMap((item) => {
                  const isAlreadyFinal = item.isCanceled === "Y" || ["RC", "CO", "CL"].includes(item.orderStatus);
                  if (isAlreadyFinal || !cancelItemIds.includes(item.fbOrderLineId)) return [item];
                  const plan = resolvePlan(item.fbOrderLineId, item.weight);
                  if (!plan) {
                    return [{ ...item, isCanceled: "Y", orderStatus: "CL" }];
                  }
                  const remarks = lineRemarks?.[item.fbOrderLineId] || globalRemarks;
                  const { remainderLine, cancelledFragment } = splitLineByQuantity(item, plan.cancelQty, taxIncludeFlag, precision, {
                    newLineId: plan.newLineId,
                    newFbOrderLineId: plan.newFbOrderLineId,
                  });
                  return [remainderLine, { ...cancelledFragment, remarks }];
                }),
                total: fbOrder.cart?.total,
                tax: fbOrder.cart?.tax,
                discount: fbOrder.cart?.discount,
                totalQty: fbOrder.cart?.totalQty,
                subTotal: fbOrder.cart?.subTotal,
                totalStd: fbOrder.cart?.totalStd,
                itemsSummery: fbOrder.cart?.itemsSummery,
                groupedItemsByTaxRate: fbOrder.cart?.groupedItemsByTaxRate,
              };
            }
          }

          if (fbOrder.cart) {
            setCart(fbOrder.cart);
          }

          const allLinesCancelled = fbOrder.lines.every((l) => l.isCanceled === "Y");
          const allCartItemsCancelled = fbOrder.cart?.items?.length ? fbOrder.cart.items.every((item) => item.isCanceled === "Y" || item.orderStatus === "CL") : true;
          const allCancelled = allLinesCancelled && allCartItemsCancelled;

          if (allCancelled) shouldClearCart = true;

          fbOrder.fbOrderStatus = allCancelled ? "CL" : "IP";
          fbOrder.isCanceled = allCancelled ? "Y" : "N";
          fbOrder.fbOrderSync = "N";

          await db.fbOrderData
            .where("fbOrderId")
            .equals(fbOrder.fbOrderId)
            .modify({
              fbOrderSync: "N",
              fbOrderStatus: fbOrder.fbOrderStatus,
              lines: fbOrder.lines,
              isCanceled: fbOrder.isCanceled,
              cart: fbOrder.cart,
              wsData: allCancelled ? {} : fbOrder.wsData,
            });

          if (fbOrder.cart) {
            setCart(fbOrder.cart);
          }

          // skipKots: this sync carries the lines' isCanceled flags to the server;
          // without it a partial void (order still IP) regenerates KOTs for every
          // remaining line and the server re-broadcasts them to the kitchen.
          await SyncData({ ...fbOrder, skipKots: true }, "upsertFbOrder", null, "", true, fbOrder.cart);

          // This order is already written as CL above, so any IP order found now
          // is a sibling (split bill / second seating). Never flip the table to
          // Available while a sibling is still live.
          const tableStillActive = await hasActiveOrdersForTable(record?.cwrFbTableId);
          const tableFreed = allCancelled && !tableStillActive;
          let remainingItems = [];
          if (allCancelled && tableStillActive) {
            const siblingOrders = await db.fbOrderData.where("cwrFbTableId").equals(record?.cwrFbTableId).toArray();
            remainingItems = siblingOrders.filter((o) => o.fbOrderStatus === "IP").flatMap((o) => o.wsData?.items || o.cart?.items || []);
          }

          const response = await db.tableData.where("cwrFbTableId").equals(record?.cwrFbTableId).toArray();
          if (!response.length) continue;

          let tableData = { ...response[0] };

          if (tableData.isSplitParent === "Y" && tableData.splitTables) {
            tableData.splitTables = tableData.splitTables.map((split) => {
              if (split.splitTable !== fbOrder.splitTable) return split;
              return {
                ...split,
                status: allCancelled ? "OPN" : "ONG",
                cart: fbOrder.cart,
              };
            });

            const currentSplit = tableData.splitTables.find((s) => s.splitTable === fbOrder.splitTable);
            if (currentSplit?.cart?.items?.length && currentSplit.cart.items.every((i) => i.isCanceled === "Y")) {
              shouldClearCart = true;
            }

            tableData.tabbleSync = "N";
            await db.tableData.put(tableData, tableData.cwrFbTableId);

            await SyncData(
              {
                fbtableStatusId: tableData.fbtableStatusId,
                cwrFbTableId: tableData.cwrFbTableId,
                splitTables: tableData.splitTables,
                statusType: tableData.statusType,
                waiter: tableData.waiter,
              },
              "upsertTableStatus"
            );
          } else {
            tableData.statusType = tableFreed ? "OPN" : "ONG";
            tableData.color = "rgba(146, 144, 152, 0.5)";
            tableData.wsData = allCancelled ? {} : fbOrder.wsData;
            tableData.items = allCancelled ? remainingItems : fbOrder.wsData?.items || [];
          }

          await SyncData(tableData, "upsertTableStatus");

          await db.tableData.update(tableData.cwrFbTableId, {
            color: "rgba(146, 144, 152, 0.5)",
            noOfPersons: tableFreed ? "" : tableData.noOfPersons,
            guestName: tableFreed ? "" : tableData.guestName,
            guestType: tableFreed ? "" : tableData.guestType,
            referredBy: tableFreed ? "" : tableData.referredBy,
            statusType: tableFreed ? "OPN" : "ONG",
            items: allCancelled ? remainingItems : fbOrder.wsData?.items || [],
            wsData: allCancelled ? {} : fbOrder.wsData,
            ordered: tableFreed ? "N" : "Y",
          });

          const updatedTableData = await db.tableData.toArray();
          if (setTableCards) setTableCards(updatedTableData);
          if (setOcupiedTablesData) setOcupiedTablesData(updatedTableData.filter((t) => t.statusType === "OCU" || t.statusType === "ONG"));
          if (setReservedTablesData) setReservedTablesData(updatedTableData.filter((t) => t.statusType === "RES"));
          if (setTableList) setTableList(updatedTableData);

          history.push("/pos");
          setKeyValue("0");
          setShowProducts(false);
        }

        if (shouldClearCart) {
          clearCart1(cart.type);
        }
      } else {
        setCategorieFlag(false);
        clearCart1(cart.type);
      }
    }
  };

  return { handleVoid, cancelling };
};

export default useVoidHandler;
